/* Twilio Voice settings page */
import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Phone, FloppyDisk, CheckCircle, WarningCircle, Lightning } from '@phosphor-icons/react';

const TwilioSettings = () => {
    const [cfg, setCfg] = useState(null);

    const load = () => api.get('/twilio/settings').then((r) => setCfg(r.data));
    useEffect(() => { load(); }, []);

    const save = async () => {
        try {
            const payload = {
                enabled: !!cfg.enabled,
                account_sid: cfg.account_sid || null,
                api_key_sid: cfg.api_key_sid || null,
                twiml_app_sid: cfg.twiml_app_sid || null,
                default_from_number: cfg.default_from_number || null,
                default_identity_prefix: cfg.default_identity_prefix || 'user_',
                auth_token: cfg._auth_token || undefined,
                api_secret: cfg._api_secret || undefined,
            };
            const r = await api.put('/twilio/settings', payload);
            setCfg(r.data);
            toast.success('Uloženo');
        } catch (e) {
            toast.error('Chyba: ' + (e.response?.data?.detail || e.message));
        }
    };

    const test = async () => {
        try {
            const r = await api.post('/twilio/test');
            r.data.ok ? toast.success(r.data.message) : toast.error(r.data.message);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Test selhal');
        }
    };

    if (!cfg) return <div className="py-10 text-center">Načítám…</div>;

    return (
        <div className="max-w-3xl" data-testid="twilio-page">
            <div className="mb-6">
                <h1 className="text-3xl font-heading font-bold flex items-center gap-3">
                    <Phone size={28} style={{ color: 'var(--brand)' }} />
                    Twilio Voice
                </h1>
                <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                    PSTN volání přes Twilio Programmable Voice (browser SDK + TwiML webhooks)
                </p>
            </div>

            <div className="rounded-xl border p-4 mb-4 text-xs"
                 style={{ background: 'rgba(59,130,246,0.1)', borderColor: '#3b82f6' }}>
                <div className="flex items-start gap-2">
                    <Lightning size={16} color="#3b82f6" className="mt-0.5" />
                    <div>
                        <b>Krok za krokem:</b><br/>
                        1. Vytvořte účet na <a href="https://www.twilio.com/try-twilio" target="_blank"
                           rel="noopener noreferrer" className="underline">twilio.com</a> (free trial $15).<br/>
                        2. Z Console zkopírujte <code>Account SID</code> + <code>Auth Token</code>.<br/>
                        3. <code>Account → API keys</code> → vytvořte API Key (Standard) → uložte SID + Secret.<br/>
                        4. <code>Voice → Manage → TwiML Apps</code> → New TwiML App. Voice Request URL nastavte na: <br/>
                        <code className="bg-[var(--bg-app)] px-2 py-0.5 rounded">
                          {window.location.origin}/api/twilio/voice/outbound</code><br/>
                        5. Koupit číslo: <code>Phone Numbers → Buy</code>, A Call Comes In webhook: <br/>
                        <code className="bg-[var(--bg-app)] px-2 py-0.5 rounded">
                          {window.location.origin}/api/twilio/voice/inbound</code><br/>
                        Status Callback URL: <code className="bg-[var(--bg-app)] px-2 py-0.5 rounded">
                          {window.location.origin}/api/twilio/voice/status</code>
                    </div>
                </div>
            </div>

            <div className="rounded-xl border p-6 mb-4"
                 style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-default)' }}>
                <label className="flex items-center gap-2 mb-4 text-sm font-bold">
                    <input
                        type="checkbox"
                        data-testid="twilio-enabled"
                        checked={!!cfg.enabled}
                        onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
                    />
                    Twilio integrace povolena
                </label>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                        ['account_sid', 'Account SID', 'ACxxxxxxxx…', false],
                        ['_auth_token', cfg.auth_token_set ? 'Auth Token (nastaveno)' : 'Auth Token',
                            cfg.auth_token_set ? '••••••••' : '', true],
                        ['api_key_sid', 'API Key SID', 'SKxxxxxxxx…', false],
                        ['_api_secret', cfg.api_secret_set ? 'API Secret (nastaveno)' : 'API Secret',
                            cfg.api_secret_set ? '••••••••' : '', true],
                        ['twiml_app_sid', 'TwiML App SID', 'APxxxxxxxx…', false],
                        ['default_from_number', 'Default From číslo (E.164)', '+1...', false],
                        ['default_identity_prefix', 'Identity prefix', 'user_', false],
                    ].map(([key, label, ph, isPwd]) => (
                        <label key={key} className="text-xs">
                            <div className="mb-1" style={{ color: 'var(--text-secondary)' }}>{label}</div>
                            <input
                                data-testid={`twilio-${key}`}
                                type={isPwd ? 'password' : 'text'}
                                value={cfg[key] || ''}
                                onChange={(e) => setCfg({ ...cfg, [key]: e.target.value })}
                                placeholder={ph}
                                className="w-full bg-[var(--bg-app)] border rounded-lg px-3 py-2 font-mono"
                                style={{ borderColor: 'var(--border-default)' }}
                            />
                        </label>
                    ))}
                </div>

                {cfg.last_test_at && (
                    <div className="mt-4 p-3 rounded text-xs flex items-center gap-2"
                         style={{
                             background: cfg.last_test_ok ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
                             color: cfg.last_test_ok ? '#22c55e' : '#ef4444',
                         }}>
                        {cfg.last_test_ok ? <CheckCircle size={14}/> : <WarningCircle size={14}/>}
                        <span>{cfg.last_test_message}</span>
                        <span className="ml-auto opacity-60">
                            {new Date(cfg.last_test_at).toLocaleString('cs-CZ')}
                        </span>
                    </div>
                )}

                <div className="flex gap-2 mt-4">
                    <button data-testid="twilio-save-btn" onClick={save}
                            className="px-4 py-2 rounded-lg font-bold text-sm"
                            style={{ background: 'var(--brand)', color: '#000' }}>
                        <FloppyDisk size={14} className="inline mr-2" /> Uložit
                    </button>
                    <button data-testid="twilio-test-btn" onClick={test}
                            className="px-4 py-2 rounded-lg border font-bold text-sm
                                      hover:bg-[var(--bg-surface-hover)]"
                            style={{ borderColor: 'var(--border-default)' }}>
                        Test spojení
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TwilioSettings;
